/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Carousel,
  Image,
  Input,
  Message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  TableColumnProps,
  Typography
} from '@arco-design/web-react';
import dayjs from 'dayjs';
import { get, post } from '../requestNew';
import './index.scss';
import { NotebookReportItem, REPORT_REASON_OPTIONS } from './types';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 30, 50];

function NotebookReport() {
  const [reporterOpenId, setReporterOpenId] = useState('');
  const [reporterUserName, setReporterUserName] = useState('');
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [dataList, setDataList] = useState<NotebookReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [commentVisible, setCommentVisible] = useState(false);
  const [commentList, setCommentList] = useState([]);
  const commentId = useRef(0);
  const currentCommentId = useRef<number | null>(null);
  const [notebookInfo, setNotebookInfo] = useState<any>(null);
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyList, setReplyList] = useState<any[]>([]);

  const getList = (opts: Record<string, any> = {}) => {
    setLoading(true);
    const params: any = {
      pageNum: opts.pageNum !== undefined ? opts.pageNum : pageNum,
      pageSize: opts.pageSize !== undefined ? opts.pageSize : pageSize
    };
    const openId = opts.reporterOpenId !== undefined ? opts.reporterOpenId : reporterOpenId.trim();
    const userName = opts.reporterUserName !== undefined ? opts.reporterUserName : reporterUserName.trim();
    const reasonValue = opts.reason !== undefined ? opts.reason : reason;
    if (openId) params.reporterOpenId = openId;
    if (userName) params.reporterUserName = userName;
    if (reasonValue) params.reason = reasonValue;

    get('/notebookReport/list', params)
      .then((res: any) => {
        if (res?.code === 200) {
          const data = res?.data ?? {};
          setDataList(data.list ?? data.records ?? []);
          setTotal(data.total ?? 0);
        } else {
          Message.error(res?.message || '查询失败');
        }
      })
      .catch(() => Message.error('网络错误'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getList();
  }, [pageNum, pageSize]);

  const onFilterQuery = () => {
    if (pageNum === 1) {
      getList({ pageNum: 1 });
    } else {
      setPageNum(1);
    }
  };

  const onReset = () => {
    setReporterOpenId('');
    setReporterUserName('');
    setReason(undefined);
    if (pageNum === 1) {
      getList({ pageNum: 1, reporterOpenId: '', reporterUserName: '', reason: undefined });
    } else {
      setPageNum(1);
    }
  };

  const showComment = (record: NotebookReportItem) => {
    if (!record.notebookId) {
      Message.error('被举报笔记ID不存在');
      return;
    }
    setCommentVisible(true);
    commentId.current = record.notebookId;
    currentCommentId.current = null;
    getNotebookInfo();
    getCommentList();
  };

  const getNotebookInfo = () => {
    get('/notebook/getNotebookInfoById', { id: commentId.current })
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setNotebookInfo(data);
        } else {
          Message.error(message || '获取笔记信息失败');
        }
      })
      .catch(() => Message.error('获取笔记信息失败!'));
  };

  const getCommentList = () => {
    get('/comment/getCommentListById', { id: commentId.current })
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setCommentList(data || []);
        } else {
          Message.error(message);
        }
      })
      .catch(() => Message.error('网络错误!'));
  };

  const closeComment = () => {
    setCommentVisible(false);
    setCommentList([]);
    setNotebookInfo(null);
    currentCommentId.current = null;
  };

  const delComment = (record: any) => {
    post(`/comment/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          if (currentCommentId.current && record.id === currentCommentId.current) {
            Message.success('删除当前评论成功');
            closeComment();
          } else {
            if (notebookInfo && notebookInfo.commentNum !== undefined) {
              setNotebookInfo({
                ...notebookInfo,
                commentNum: Math.max(0, (notebookInfo.commentNum || 0) - 1)
              });
            }
            getCommentList();
          }
        } else {
          Message.error(message);
        }
      })
      .catch(() => Message.error('网络错误!'));
  };

  const showReply = (record: any) => {
    setReplyList(record.replyList || []);
    setReplyVisible(true);
  };

  const closeReply = () => {
    setReplyVisible(false);
    setReplyList([]);
  };

  const delReply = (record: any) => {
    post(`/comment/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          closeReply();
          getCommentList();
        } else {
          Message.error(message);
        }
      })
      .catch(() => Message.error('网络错误!'));
  };

  const commentColumns: TableColumnProps[] = [
    {
      title: '评论选手',
      dataIndex: 'gameName',
      width: 100,
      render: (text, record: any) => {
        const isCurrentComment = currentCommentId.current && record.id === currentCommentId.current;
        return (
          <Typography.Text style={{ fontWeight: isCurrentComment ? 'bold' : 'normal', color: isCurrentComment ? '#000' : '#888' }}>
            {text}
          </Typography.Text>
        );
      }
    },
    {
      title: '评论内容',
      dataIndex: 'content',
      width: 300,
      render: (text, record: any) => {
        const isCurrentComment = currentCommentId.current && record.id === currentCommentId.current;
        return (
          <Typography.Text style={{ fontWeight: isCurrentComment ? 'bold' : 'normal' }}>
            {text}
          </Typography.Text>
        );
      }
    },
    {
      title: '评论时间',
      dataIndex: 'createTime',
      width: 150,
      render: text => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '回复数',
      dataIndex: 'replyNum',
      width: 80,
      render: text => text || 0
    },
    {
      title: '操作',
      dataIndex: 'commentAction',
      width: 150,
      render: (_, record: any) => {
        const isCurrentComment = currentCommentId.current && record.id === currentCommentId.current;
        return (
          <>
            <Button
              className="btn"
              type="text"
              disabled={!record.replyList || record.replyList.length === 0}
              onClick={() => showReply(record)}
            >
              回复管理
            </Button>
            <Popconfirm
              focusLock
              title="提示"
              content={isCurrentComment ? '是否删除当前评论?' : '是否删除?'}
              onOk={() => delComment(record)}
            >
              <Button className="btn" type="text" status="danger">
                删除
              </Button>
            </Popconfirm>
          </>
        );
      }
    }
  ];

  const replyColumns: TableColumnProps[] = [
    {
      title: '回复人',
      dataIndex: 'gameName',
      width: 100
    },
    {
      title: '被回复',
      dataIndex: 'replyToGameName',
      width: 100,
      render: text => (text ? `@${text}` : '--')
    },
    {
      title: '内容',
      dataIndex: 'content',
      width: 300
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      width: 150,
      render: text => (text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '操作',
      dataIndex: 'replyAction',
      width: 80,
      render: (_, record: any) => (
        <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => delReply(record)}>
          <Button className="btn" type="text" status="danger">
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  const columns: TableColumnProps<NotebookReportItem>[] = [
    {
      title: '举报人',
      dataIndex: 'reporterUserName',
      width: 120,
      render: v => v || '-'
    },
    {
      title: '举报人openId',
      dataIndex: 'reporterOpenId',
      width: 220,
      render: v =>
        v ? (
          <Typography.Text copyable style={{ margin: 0 }}>
            {v}
          </Typography.Text>
        ) : (
          '-'
        )
    },
    {
      title: '笔记ID',
      dataIndex: 'notebookId',
      width: 110
    },
    {
      title: '举报类目',
      dataIndex: 'category',
      width: 120,
      render: v => v || '-'
    },
    {
      title: '举报原因',
      dataIndex: 'reason',
      width: 180,
      render: v => v || '-'
    },
    {
      title: '举报描述',
      dataIndex: 'content',
      width: 240,
      render: v => v || '-'
    },
    {
      title: '举报截图',
      dataIndex: 'picture',
      width: 180,
      render: (pics: string[] | undefined) => {
        if (!pics || pics.length === 0) {
          return '-';
        }
        return (
          <div className="picture-list">
            {pics.map((src, index) => (
              <Image key={index} src={src} width={48} height={48} preview />
            ))}
          </div>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      render: v => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      width: 180,
      render: v => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-')
    },
    {
      title: '操作',
      dataIndex: 'op',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Button className="btn" type="text" onClick={() => showComment(record)}>
          评论管理
        </Button>
      )
    }
  ];

  return (
    <div className="notebook-report table-flex">
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          className="filter-item"
          style={{ width: 220 }}
          allowClear
          placeholder="举报人openId"
          value={reporterOpenId}
          onChange={setReporterOpenId}
        />
        <Input
          className="filter-item"
          style={{ width: 180 }}
          allowClear
          placeholder="举报人名称"
          value={reporterUserName}
          onChange={setReporterUserName}
        />
        <Select
          className="filter-item"
          style={{ width: 240 }}
          allowClear
          placeholder="举报原因"
          value={reason}
          onChange={setReason}
        >
          {REPORT_REASON_OPTIONS.map(item => (
            <Select.Option key={item} value={item}>
              {item}
            </Select.Option>
          ))}
        </Select>
        <Button type="primary" onClick={onFilterQuery}>
          查询
        </Button>
        <Button onClick={onReset}>重置</Button>
      </Space>

      <div className="table-content">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          data={dataList}
          className="table-scroll"
          pagination={{
            sizeCanChange: true,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            sizeOptions: PAGE_SIZE_OPTIONS,
            onChange: (num, size) => {
              setPageNum(num);
              setPageSize(size);
            }
          }}
          scroll={{ y: '100%', x: 1700 }}
        />
      </div>

      <Modal
        title="评论管理"
        visible={commentVisible}
        style={{ width: 800 }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={closeComment}
        onOk={closeComment}
        unmountOnExit
      >
        {notebookInfo && (
          <div
            style={{
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              background: '#fff'
            }}
          >
            <div style={{ marginBottom: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', marginBottom: '4px' }}>
                  <img
                    src={notebookInfo.profilePic || 'https://fulicupdev.oss-cn-hangzhou.aliyuncs.com/profilePic/defaultProfile.png'}
                    alt="头像"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      marginRight: '12px',
                      objectFit: 'cover'
                    }}
                  />
                  <Typography.Text style={{ fontSize: '14px', marginRight: '8px', fontWeight: 'bold', lineHeight: '1.5' }}>
                    {notebookInfo.gameName || '未知用户'}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: '14px', lineHeight: '1.5', color: '#333' }}>
                    {notebookInfo.createTime ? dayjs(notebookInfo.createTime).format('MM-DD HH:mm') : ''}
                  </Typography.Text>
                </div>
                {notebookInfo.title && (
                  <Typography.Text style={{ fontSize: '15px', fontWeight: '500', marginBottom: '8px', marginTop: '12px', display: 'block' }}>
                    {notebookInfo.title}
                  </Typography.Text>
                )}
                {notebookInfo.content && (
                  <Typography.Text style={{ fontSize: '14px', marginBottom: '12px', display: 'block', lineHeight: '1.5', color: '#333' }}>
                    {notebookInfo.content}
                  </Typography.Text>
                )}
                {notebookInfo.picture && Array.isArray(notebookInfo.picture) && notebookInfo.picture.length > 0 && (
                  <div className="picture-container">
                    {notebookInfo.picture.length === 1 ? (
                      <Image src={notebookInfo.picture[0]} alt="笔记图片" preview />
                    ) : (
                      <Carousel autoPlay={false} indicatorPosition="outer" style={{ width: '100%' }}>
                        {notebookInfo.picture.map((pic: string, index: number) => (
                          <div key={index} style={{ textAlign: 'center', width: '100%' }}>
                            <Image src={pic} alt={`笔记图片${index + 1}`} preview />
                          </div>
                        ))}
                      </Carousel>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    👍 点赞量{notebookInfo.likeNum || 0}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    💬 评论量{notebookInfo.commentNum || 0}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </div>
        )}
        <div style={{ height: notebookInfo ? 'calc(70vh - 200px)' : '70vh' }}>
          <Table
            rowKey="id"
            columns={commentColumns}
            className="table-scroll"
            data={commentList}
            pagination={false}
            scroll={{ y: '100%', x: '100%' }}
          />
        </div>
      </Modal>
      <Modal
        title="回复管理"
        visible={replyVisible}
        style={{ width: 1000 }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={closeReply}
        onOk={closeReply}
        unmountOnExit
      >
        <Table rowKey="id" columns={replyColumns} data={replyList} pagination={false} scroll={{ y: 1000 }} />
      </Modal>
    </div>
  );
}

export default NotebookReport;
